/**
 * Product Multi-Select Component for Medical On Demand
 *
 * Filter chip pattern for selecting one or more products.
 * Uses the same primary color scheme as inquiry label chips for brand consistency.
 */

import { AlertCircleIcon } from 'lucide-react-native';
import { Controller, Control, FieldErrors } from 'react-hook-form';

import { CheckboxCard } from './CheckboxCard';

import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { type Product } from '@/constants/medicalOnDemand';
import type { MODFormData } from '@/hooks/useMODInquiryForm';

type ProductSelectDropdownProps = {
  products: Product[];
  control: Control<MODFormData>;
  errors: FieldErrors<MODFormData>;
  isDisabled?: boolean;
  onProductChange?: (productId: string) => void;
};

export function ProductSelectDropdown({
  products,
  control,
  errors,
  isDisabled = false,
  onProductChange,
}: ProductSelectDropdownProps) {
  /** Sort products alphabetically by brand name. */
  const sortedProducts = [...products].sort((a, b) => a.brandName.localeCompare(b.brandName));

  return (
    <Controller
      name="productIds"
      control={control}
      render={({ field: { onChange, value }, fieldState, formState }) => {
        const selectedIds = (value ?? []) as string[];

        const showError =
          !!errors.productIds?.message && (fieldState.isTouched || formState.isSubmitted);

        const toggleProduct = (productId: string) => {
          if (isDisabled) {
            return;
          }

          const nextSelectedIds = selectedIds.includes(productId)
            ? selectedIds.filter((id) => id !== productId)
            : [...selectedIds, productId];

          onChange(nextSelectedIds);

          onProductChange?.(productId);
        };

        return (
          <FormControl isInvalid={showError} isDisabled={isDisabled}>
            <HStack className="mb-2 items-center justify-between">
              <Text className="font-semibold text-sm text-typography-950">Product(s)*</Text>
              <Text className="text-xs text-typography-500">* Required</Text>
            </HStack>

            <HStack className="w-full flex-col items-stretch gap-3 md:flex-row md:flex-wrap md:items-start">
              {sortedProducts.map((product) => (
                <CheckboxCard
                  key={product.id}
                  testID={`product-chip-${product.id}`}
                  label={product.brandName}
                  inlineSubtitle={`(${product.genericName})`}
                  isSelected={selectedIds.includes(product.id)}
                  isDisabled={isDisabled}
                  onSelect={() => toggleProduct(product.id)}
                  accessibilityLabel={`${product.brandName} (${product.genericName})`}
                  className="md:w-[260px] md:self-start"
                />
              ))}
            </HStack>

            <FormControlError className="mt-2">
              <FormControlErrorIcon as={AlertCircleIcon} />
              <FormControlErrorText size="xs">{errors.productIds?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
